
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CompletedBooking } from "@/hooks/useCompletedBookings";

interface PaymentMetricCardProps {
  completions: CompletedBooking[];
}

export function PaymentMetricCard({ completions }: PaymentMetricCardProps) {
  const metrics = useMemo(() => {
    const totalAmount = completions.reduce((sum, completion) => sum + completion.payment_amount, 0);
    const paidCount = completions.filter(c => c.payment_status === 'paid').length;
    const pendingCount = completions.filter(c => c.payment_status !== 'paid').length;
    const averageRating = completions.length 
      ? completions.reduce((sum, c) => sum + c.rating, 0) / completions.length
      : 0;
      
    return {
      totalAmount,
      paidCount,
      pendingCount,
      averageRating: averageRating.toFixed(1),
      totalCompletions: completions.length
    };
  }, [completions]);

  return (
    <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-100">
      <CardHeader className="pb-1 pt-3">
        <CardTitle className="text-base font-semibold text-purple-800">Payment Summary</CardTitle>
      </CardHeader>
      <CardContent className="pt-2 pb-3">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 text-sm">
          <div className="space-y-1">
            <p className="text-xs text-gray-500">Total Payments</p>
            <p className="text-xl font-bold text-purple-700">{metrics.totalAmount.toFixed(2)}</p>
          </div>
          
          <div className="space-y-1">
            <p className="text-xs text-gray-500">Completed Bookings</p>
            <p className="text-xl font-bold text-purple-700">{metrics.totalCompletions}</p>
          </div>
          
          <div className="space-y-1">
            <p className="text-xs text-gray-500">Paid Status</p>
            <p className="text-xl font-bold text-green-600">{metrics.paidCount} <span className="text-xs font-normal text-gray-500">paid</span></p>
            {metrics.pendingCount > 0 && (
              <p className="text-xs text-amber-600">{metrics.pendingCount} pending</p>
            )}
          </div>
          
          <div className="space-y-1">
            <p className="text-xs text-gray-500">Average Rating</p>
            <p className="text-xl font-bold text-blue-600">{metrics.averageRating} <span className="text-xs font-normal text-gray-500">/ 5</span></p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
